import { motion, useInView } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { config } from '@/api/config';
import { Button } from '@/components/ui';
import { NewsletterForm } from '@/features/newsletter/components/NewsletterForm';

// Abstract art component for the magazine covers
const AbstractCoverArt = ({ variant }: { variant: 1 | 2 | 3 }) => {
  return (
    <div className="absolute inset-0 overflow-hidden rounded-sm bg-[#F4F2F2]">
      {variant === 1 && (
        <>
          <div className="absolute top-10 left-[-20%] h-32 w-32 rounded-full bg-[#E94E1B]/80 mix-blend-multiply" />
          <div className="absolute bottom-10 right-[-10%] h-40 w-40 rounded-full bg-[#2E2A23]/20" />
          <div className="absolute top-[40%] left-[20%] h-24 w-4 bg-[#2E2A23] rotate-12" />
          <div className="absolute bottom-20 left-10 h-16 w-16 bg-[#CAC7C7] rounded-t-full" />
        </>
      )}
      {variant === 2 && (
        <>
          <div className="absolute top-1/2 left-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 border-[20px] border-[#E94E1B]/40 rounded-full" />
          <div className="absolute top-10 right-4 h-20 w-12 bg-[#2E2A23] rounded-t-xl opacity-80" />
          <div className="absolute bottom-0 left-0 w-full h-1/3 bg-[#CAC7C7]/50 transform -skew-y-6 origin-bottom-left" />
        </>
      )}
      {variant === 3 && (
        <>
          <div className="absolute inset-x-8 top-12 bottom-24 border-2 border-[#2E2A23]/20" />
          <div className="absolute top-20 right-[-10%] h-24 w-24 rounded-full bg-[#E94E1B]/60" />
          <div className="absolute bottom-12 left-8 h-20 w-32 bg-[#2E2A23]/40" />
          <div className="absolute top-1/3 left-1/3 w-2 h-24 bg-[#2E2A23] rotate-45" />
        </>
      )}
    </div>
  );
};

const MagazineCard = ({
  title = 'Newsletter',
  variant = 1,
  className = '',
  style = {},
}: {
  title?: string;
  variant?: 1 | 2 | 3;
  className?: string;
  style?: React.CSSProperties;
}) => {
  return (
    <div
      className={`relative flex flex-col bg-[#F4F2F2] shadow-2xl ${className}`}
      style={{
        width: '240px',
        height: '320px',
        ...style,
      }}
    >
      <div className="relative h-full w-full p-4 flex flex-col items-center">
        <h3 className="font-serif text-2xl text-[#2E2A23] tracking-tight mb-4 z-10">{title}</h3>
        <div className="relative flex-1 w-full mb-6">
          <AbstractCoverArt variant={variant} />
        </div>
        <div className="w-full space-y-1.5 opacity-40">
          <div className="h-1 w-full bg-[#2E2A23] rounded-full" />
          <div className="h-1 w-[90%] bg-[#2E2A23] rounded-full" />
          <div className="h-1 w-[60%] bg-[#2E2A23] rounded-full" />
        </div>
      </div>
    </div>
  );
};

// Abstract icon/shape components for feature cards
const AbstractIcon = ({ variant }: { variant: 'buscamos' | 'filtramos' | 'essencial' }) => {
  return (
    <div className="relative w-24 h-24 sm:w-32 sm:h-32">
      {variant === 'buscamos' && (
        <>
          <div className="absolute top-2 left-3 w-8 h-8 rounded-full bg-[#E94E1B] opacity-60" />
          <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-[#2E2A23] opacity-40" />
          <div className="absolute bottom-6 left-6 w-5 h-5 rounded-full bg-[#E94E1B] opacity-50" />
          <div className="absolute top-8 left-12 w-10 h-6 bg-[#E94E1B] opacity-45 rotate-12" />
          <div className="absolute bottom-4 right-2 w-8 h-4 bg-[#2E2A23] opacity-35 -rotate-6" />
          <div className="absolute top-12 right-8 w-7 h-10 bg-[#E94E1B] opacity-55 rounded-tl-full rounded-br-full rotate-45" />
          <div className="absolute bottom-2 left-2 w-6 h-8 bg-[#E94E1B] opacity-30 rounded-tr-full rounded-bl-full -rotate-12" />
          <div className="absolute top-6 left-16 w-4 h-4 bg-[#2E2A23] opacity-50 rounded-sm rotate-45" />
        </>
      )}
      {variant === 'filtramos' && (
        <>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-20 h-16 border-4 border-[#E94E1B] opacity-70 rounded-b-full" />
          <div className="absolute top-1 left-2 w-6 h-6 rounded-full bg-[#2E2A23] opacity-10" />
          <div className="absolute top-2 right-3 w-7 h-5 bg-[#E94E1B] opacity-10 rotate-12" />
          <div className="absolute top-8 left-1 w-5 h-7 bg-[#E94E1B] opacity-10 rounded-tr-full" />
          <div className="absolute top-6 right-1 w-4 h-4 bg-[#2E2A23] opacity-10" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#E94E1B] opacity-90" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 translate-x-4 translate-y-2 w-6 h-4 bg-[#E94E1B] opacity-85 rotate-6" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -translate-x-4 translate-y-1 w-5 h-5 bg-[#2E2A23] opacity-80 rounded-sm" />
        </>
      )}
      {variant === 'essencial' && (
        <>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 translate-y-2 w-16 h-14 border-[3px] border-[#E94E1B] rounded-t-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -translate-y-6 w-8 h-8 rounded-full bg-[#E94E1B] opacity-90" />
        </>
      )}
    </div>
  );
};

// Feature card component
const FeatureCard = ({
  title,
  description,
  variant,
  index,
}: {
  title: string;
  description: string;
  variant: 'buscamos' | 'filtramos' | 'essencial';
  index: number;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{
        duration: 0.8,
        delay: index * 0.2,
        ease: [0.21, 0.47, 0.32, 0.98],
      }}
      className="relative bg-[#F4F2F2] p-8 sm:p-10 md:p-12 rounded-sm shadow-lg hover:shadow-2xl transition-shadow duration-500 group overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-[#E94E1B]/5 rounded-full blur-3xl transition-all duration-500 group-hover:scale-150" />

      <div className="relative z-10 space-y-6">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }}
          transition={{
            duration: 0.6,
            delay: index * 0.2 + 0.3,
            ease: 'easeOut',
          }}
        >
          <AbstractIcon variant={variant} />
        </motion.div>

        <h3 className="font-serif text-3xl sm:text-4xl text-[#2E2A23] leading-tight">{title}</h3>

        <p className="text-[#888185] text-base sm:text-lg leading-relaxed font-normal">
          {description}
        </p>

        <motion.div
          initial={{ width: 0 }}
          animate={isInView ? { width: '60px' } : { width: 0 }}
          transition={{ duration: 0.8, delay: index * 0.2 + 0.5 }}
          className="h-1 bg-[#E94E1B] rounded-full"
        />
      </div>
    </motion.div>
  );
};

export function LandingPage() {
  const { t } = useTranslation();
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, { once: true, margin: '-50px' });

  const isNewsletterOnly = config.newsletter.enabled;

  const features = [
    {
      title: t('landing.feature1Title'),
      description: t('landing.feature1Desc'),
      variant: 'buscamos' as const,
    },
    {
      title: t('landing.feature2Title'),
      description: t('landing.feature2Desc'),
      variant: 'filtramos' as const,
    },
    {
      title: t('landing.feature3Title'),
      description: t('landing.feature3Desc'),
      variant: 'essencial' as const,
    },
  ];

  return (
    <div className="relative min-h-screen bg-[#F4F2F2]">
      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-[#F4F2F2]/80 backdrop-blur-sm border-b border-[#CAC7C7]">
        <nav className="w-full px-8 py-4 flex justify-between items-center max-w-7xl mx-auto">
          <Link
            to="/"
            className="text-2xl font-serif text-[#2E2A23] tracking-tight hover:text-[#E94E1B] transition-colors"
          >
            Newsletter
          </Link>
          <div className="flex items-center gap-8 text-[#888185] text-sm font-medium tracking-wide">
            <a href="#why" className="hover:text-[#E94E1B] transition-colors">
              {t('landing.ctaSecondary')}
            </a>
            {!isNewsletterOnly && (
              <Link to="/login" className="hover:text-[#E94E1B] transition-colors">
                {t('auth.signIn')}
              </Link>
            )}
          </div>
        </nav>
      </header>

      <main>
        {/* Hero Section with Magazines */}
        <section className="relative overflow-hidden">
          <div className="flex flex-col lg:flex-row items-center justify-center w-full max-w-7xl mx-auto px-8 py-12 lg:py-20">
            {/* Left Column: Text */}
            <div className="flex-1 w-full lg:w-1/2 z-20 flex flex-col items-start justify-center lg:pr-12 mb-16 lg:mb-0">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.1] text-[#2E2A23] mb-8"
              >
                {t('landing.heroTitle')}
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                className="space-y-8"
              >
                <p className="text-[#888185] text-lg md:text-xl font-normal leading-relaxed">
                  {isNewsletterOnly ? t('newsletter.subtitle') : t('landing.heroSubtitle')}
                </p>

                {isNewsletterOnly ? (
                  <NewsletterForm />
                ) : (
                  <Link to="/feed">
                    <Button size="lg" className="rounded-full">
                      {t('landing.heroButton')}
                    </Button>
                  </Link>
                )}
              </motion.div>
            </div>

            {/* Right Column: Magazines */}
            <div className="flex-1 w-full lg:w-1/2 h-[500px] relative flex items-center justify-center">
              {/* Magazine 3 (Back) */}
              <motion.div
                initial={{ opacity: 0, x: 50, y: 20, rotate: 10 }}
                animate={{ opacity: 1, x: 140, y: -20, rotate: 12 }}
                transition={{ duration: 1, delay: 0.4, ease: 'easeOut' }}
                className="absolute z-10 hidden lg:block"
              >
                <MagazineCard
                  title="Newsletter"
                  variant={3}
                  className="opacity-90 blur-[0.5px] scale-90"
                />
              </motion.div>

              {/* Magazine 2 (Middle) */}
              <motion.div
                initial={{ opacity: 0, x: 20, y: 40, rotate: -5 }}
                animate={{ opacity: 1, x: -80, y: 60, rotate: -15 }}
                transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
                className="absolute z-20 hidden lg:block"
              >
                <MagazineCard
                  title="Newsletter"
                  variant={2}
                  className="opacity-95 blur-[0.3px] scale-95"
                />
              </motion.div>

              {/* Magazine 1 (Front) */}
              <motion.div
                initial={{ opacity: 0, y: 50, rotate: 0 }}
                animate={{ opacity: 1, y: 0, rotate: -5 }}
                transition={{ duration: 1, delay: 0.2, type: 'spring', stiffness: 50 }}
                className="relative z-30"
              >
                <MagazineCard
                  title="Newsletter"
                  variant={1}
                  className="shadow-[0_20px_50px_rgba(0,0,0,0.15)]"
                />
              </motion.div>

              {/* Mobile Only Simplified View */}
              <div className="lg:hidden relative z-30">
                <MagazineCard
                  title="Newsletter"
                  variant={1}
                  className="shadow-[0_20px_50px_rgba(0,0,0,0.15)] rotate-[-5deg]"
                />
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="w-full flex justify-center pb-8"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
            >
              <ArrowDown className="text-[#888185] w-6 h-6 stroke-[1.5]" />
            </motion.div>
          </motion.div>

          {/* Background Decorative Gradient/blur */}
          <div className="absolute top-[-20%] right-[-10%] w-[50vw] h-[50vh] bg-[#E94E1B]/5 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-[-10%] left-[-10%] w-[40vw] h-[40vh] bg-[#2E2A23]/5 rounded-full blur-[100px] pointer-events-none" />
        </section>

        {/* Features Section */}
        <section id="why" className="w-full max-w-7xl mx-auto px-8 py-16 sm:py-20 md:py-24">
          {/* Section Header */}
          <motion.div
            ref={headerRef}
            initial={{ opacity: 0, y: 40 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="text-center mb-16 sm:mb-20 md:mb-24 space-y-6"
          >
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.1] text-[#2E2A23]">
              {t('landing.whyTitle')}
            </h2>
            <p className="text-[#888185] text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed">
              {t('landing.whySubtitle')}
            </p>
          </motion.div>

          {/* Editorial Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 mb-12">
            <div className="lg:col-span-7">
              <FeatureCard
                title={features[0].title}
                description={features[0].description}
                variant={features[0].variant}
                index={0}
              />
            </div>
            <div className="lg:col-span-5">
              <FeatureCard
                title={features[1].title}
                description={features[1].description}
                variant={features[1].variant}
                index={1}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
            <div className="lg:col-span-12">
              <FeatureCard
                title={features[2].title}
                description={features[2].description}
                variant={features[2].variant}
                index={2}
              />
            </div>
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-20 sm:mt-24 md:mt-32 text-center space-y-8"
          >
            <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2E2A23] leading-tight">
              {isNewsletterOnly ? t('newsletter.title') : t('landing.ctaTitle')}
            </h3>

            {isNewsletterOnly ? (
              <div className="flex justify-center">
                <NewsletterForm />
              </div>
            ) : (
              <Link to="/login?mode=signup">
                <Button size="lg" className="rounded-full">
                  {t('landing.ctaButton')}
                </Button>
              </Link>
            )}
          </motion.div>
        </section>
      </main>

      {/* Footer */}
      <footer className="px-6 py-12 border-t border-[#CAC7C7]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src="/img/logo-full.svg" alt="Newsletter" className="h-10" />
            <div className="text-sm text-[#888185]">
              © {new Date().getFullYear()} Vector Labs. {t('landing.footerRights')}
            </div>
          </div>
        </div>
      </footer>

      {/* Background Decorative Elements */}
      <div className="absolute top-[30%] right-[5%] w-32 h-32 border-2 border-[#E94E1B]/10 rounded-full pointer-events-none hidden lg:block" />
      <div className="absolute top-[60%] left-[8%] w-24 h-24 bg-[#E94E1B]/5 rotate-45 pointer-events-none hidden lg:block" />
    </div>
  );
}
